<script setup lang="ts">
import { useToast } from '@/shared/composables/UseToast'
import { SandboxDataSet, SandboxGenerationMode } from '@swipe4work/models-orval'
import { generateSandboxData } from '@swipe4work/api-orval'
import { useCompanyStore } from '@/shared/stores/CompanyStore'
import { useDynamicDialogLifecycle } from '@/shared/composables/UseDynamicDialogLifecycle'
import { useConfirm } from 'primevue/useconfirm'
import { useViteEnvironment } from '@/shared/composables/useViteEnvironment'

const toast = useToast()

const { closeDialog, dialogData } = useDynamicDialogLifecycle<{
  companyId: number,
  companyName: string,
}>()

const viteEnvironment = useViteEnvironment()

const allowedToGenerateSandbox = computed(() => {
  return !viteEnvironment.isProd
})

onBeforeMount(() => {
  if (!allowedToGenerateSandbox.value) {
    toast.error('Toegang geweigerd', 'Deze functionaliteit is niet beschikbaar in de productieomgeving.')
    closeDialog()
  }
})

const companyStore = useCompanyStore()
const confirm = useConfirm()

const isGenerating = ref<boolean>(false)
const selectedDataSet = ref<SandboxDataSet | null>(null)
const openInNewTab = ref<boolean>(false)
const generationMode = ref<SandboxGenerationMode>(SandboxGenerationMode.Full)
const recalculateAllPlatformMatchScores = ref<boolean>(false)

const generationModes = [
  {
    value: SandboxGenerationMode.Full,
    name: 'Volledig (kandidaten + vacatures + sollicitaties)', 
  },
  {
    value: SandboxGenerationMode.CandidatesOnly,
    name: 'Alleen kandidaten', 
  },
  {
    value: SandboxGenerationMode.JobsOnly,
    name: 'Alleen vacatures', 
  },
]

function onGenerate() {
  if (!selectedDataSet.value) {
    toast.error('Validatiefout', 'Selecteer een sandbox dataset')

    return
  }

  confirm.require({
    message: 'Weet je zeker dat je sandbox data wilt genereren voor dit bedrijf? Dit kan niet ongedaan worden gemaakt.',
    header: 'Weet je het zeker?',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Annuleren',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Ja, genereren',
    },
    reject: () => {
      closeDialog()
    },
    accept: async () => {
      isGenerating.value = true

      generateSandboxData({
        companyId: dialogData.value.companyId!,
        companyName: dialogData.value.companyName!,
        sandboxDataSet: selectedDataSet.value!,
        generationMode: generationMode.value,
        recalculateAllPlatformMatchScores: recalculateAllPlatformMatchScores.value,
      })
        .then(async () => {
          toast.success('Success', 'De sandbox data is succesvol gegenereerd')
          if (openInNewTab.value) {
            await companyStore.forwardToPortal(dialogData.value.companyId!)
          }
          closeDialog()
        })
        .catch(() => {
          toast.error('Error', 'Er is iets misgegaan bij het genereren van de sandbox data')
        })
        .finally(() => {
          isGenerating.value = false
        })
    },
  })
}

const sandboxDataSets = [
  {
    value: SandboxDataSet.FinanceHrAdministration,
    name: 'Finance, HR & administratie', 
  },
  {
    value: SandboxDataSet.It,
    name: 'ICT', 
  },
  {
    value: SandboxDataSet.HospitalityRecreation,
    name: 'Horeca & recreatie', 
  },
  {
    value: SandboxDataSet.MarketingCommunication,
    name: 'Marketing & communicatie', 
  },
  {
    value: SandboxDataSet.SalesProcurement,
    name: 'Sales & inkoop', 
  },
  {
    value: SandboxDataSet.PolicyOrganization,
    name: 'Beleid & organisatie', 
  },
  {
    value: SandboxDataSet.Healthcare,
    name: 'Zorg & welzijn', 
  },
  {
    value: SandboxDataSet.TechProductionLogistics,
    name: 'Techniek, productie & logistiek', 
  },
  {
    value: SandboxDataSet.MatchingEvaluation,
    name: 'Matching Evaluatie (cross-category)', 
  },
]
</script>

<template>
  <section class="min-w-lg flex flex-col gap-y-7.5">
    <h2 class="mt-5">
      Genereer Sandbox Data
    </h2>

    <div
      v-if="!isGenerating"
      class="w-full flex flex-col gap-4"
    >
      <IftaLabel>
        <Select
          v-model="selectedDataSet"
          class="w-full"
          :options="sandboxDataSets"
          option-label="name"
          option-value="value"
          placeholder="Selecteer een dataset"
        />
        <label for="sandboxDataSet">Dataset</label>
      </IftaLabel>

      <IftaLabel>
        <Select
          v-model="generationMode"
          class="w-full"
          :options="generationModes"
          option-label="name"
          option-value="value"
          input-id="generationMode"
        />
        <label for="generationMode">Wat genereren?</label>
      </IftaLabel>

      <div class="flex items-center gap-3">
        <ToggleSwitch
          v-model="recalculateAllPlatformMatchScores"
          input-id="recalculateAllPlatformMatchScores"
        />
        <label for="recalculateAllPlatformMatchScores" class="cursor-pointer select-none text-sm">
          Matchscores opnieuw berekenen voor alle actieve vacatures in het platform
        </label>
      </div>

      <div class="flex items-center gap-3">
        <ToggleSwitch
          v-model="openInNewTab"
          input-id="openInNewTab"
        />
        <label for="openInNewTab" class="cursor-pointer select-none text-sm">
          Na genereren portaal openen in nieuw tabblad
        </label>
      </div>

      <div class="flex justify-end gap-x-3">
        <Button
          label="Sluiten"
          severity="secondary"
          @click="closeDialog"
        />
        <Button
          type="button"
          label="Genereer"
          :disabled="!selectedDataSet"
          @click="onGenerate"
        />
      </div>
    </div>
    <div
      v-else
      class="flex flex-col items-center justify-center gap-y-4"
    >
      <ProgressSpinner />
      <p>
        De sandbox data wordt gegenereerd. Dit kan enkele minuten duren.
      </p>
    </div>
  </section>
</template>
